import { useEffect, useState } from 'react'
import { BekatToolkitSummaryCard } from '@/components/BekatToolkitSummaryCard'
import { DetailRow } from '@/components/DetailRow'
import { PostureExclusionsCard } from '@/components/PostureExclusionsCard'
import { TrafficLightSummary } from '@/components/TrafficLightSummary'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { fetchPostureThresholds } from '@/api/posture'
import { formatDateTime } from '@/lib/formatAgent'
import { evaluateOverviewPosture, OVERVIEW_HARDWARE_SECTION_IDS } from '@/lib/overviewTrafficLight'
import { DEFAULT_POSTURE_THRESHOLDS, loadPostureThresholds } from '@/lib/postureThresholds'

const SECTION_TABS = {
  security: 'security',
}

const TOOLKIT_PANEL_ID = 'overview-toolkit-summary'

export function AgentOverview({
  agent,
  hardware = [],
  rmmData,
  toolkitSummary,
  onNavigateTab,
  onAgentPatch,
}) {
  const [systemOpen, setSystemOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [toolkitOpen, setToolkitOpen] = useState(false)
  const [thresholds, setThresholds] = useState(DEFAULT_POSTURE_THRESHOLDS)

  useEffect(() => {
    let cancelled = false
    loadPostureThresholds(fetchPostureThresholds).then((data) => {
      if (!cancelled) setThresholds(data)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const posture = evaluateOverviewPosture({
    agent,
    hardware,
    rmmData,
    toolkitSummary,
    thresholds,
  })

  function handleSectionClick(sectionId) {
    if (sectionId === 'toolkit') {
      setToolkitOpen(true)
      requestAnimationFrame(() => {
        document
          .getElementById(TOOLKIT_PANEL_ID)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      return
    }
    if (OVERVIEW_HARDWARE_SECTION_IDS.has(sectionId) || sectionId === 'hardware') {
      onNavigateTab?.('hardware')
      return
    }
    if (sectionId === 'connectivity') {
      onNavigateTab?.('network')
      requestAnimationFrame(() => {
        document
          .getElementById('network-atera-connectivity')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      return
    }
    const tab = SECTION_TABS[sectionId]
    if (tab) onNavigateTab?.(tab)
  }

  const systemSummary = [
    agent.os_name,
    agent.machine_type,
    agent.domain,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="space-y-4">
      <TrafficLightSummary
        title="Device at a glance"
        posture={posture}
        onSectionClick={handleSectionClick}
      />

      <BekatToolkitSummaryCard
        summary={toolkitSummary}
        open={toolkitOpen}
        onOpenChange={setToolkitOpen}
        panelId={TOOLKIT_PANEL_ID}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <CollapsePanel
          id="overview-system"
          title="System"
          collapsedSummary={systemSummary || undefined}
          open={systemOpen}
          onOpenChange={setSystemOpen}
          bodyClassName="space-y-2 text-sm"
        >
          <DetailRow label="OS" value={agent.os_name} />
          <DetailRow label="Version" value={agent.os_version} />
          <DetailRow label="Build" value={agent.os_build} />
          <DetailRow label="Type" value={agent.machine_type} />
          <DetailRow label="Domain" value={agent.domain} />
          <DetailRow label="Last user" value={agent.last_logged_user} />
          <DetailRow label="Last seen" value={formatDateTime(agent.last_seen)} />
          <DetailRow label="Last reboot" value={formatDateTime(agent.last_reboot_time)} />
          <DetailRow label="Folder" value={agent.folder_name} />
          <DetailRow label="Description" value={agent.computer_description} />
          <DetailRow label="AV product" value={agent.av_product} />
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">AV status</span>
            <span className="text-right font-medium">
              {agent.av_status === 'protected'
                ? 'Protected'
                : agent.av_status === 'not_detected'
                  ? 'Not detected'
                  : agent.av_status === 'no_data' || !agent.av_status
                    ? 'No software data'
                    : agent.av_status}
            </span>
          </div>
          <div className="flex justify-between gap-4 pt-1">
            <span className="text-muted-foreground">Antivirus detail</span>
            <button
              type="button"
              className="text-right text-sm font-medium text-primary hover:underline"
              onClick={() => onNavigateTab?.('security')}
            >
              Open Security tab
            </button>
          </div>
          <DetailRow label="Created (Atera)" value={formatDateTime(agent.created_at_atera)} />
          <DetailRow label="Modified (Atera)" value={formatDateTime(agent.modified_at_atera)} />
          <div className="flex justify-between gap-4 pt-1">
            <span className="text-muted-foreground">Atera link</span>
            {agent.app_view_url ? (
              <a
                href={agent.app_view_url}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline text-right"
              >
                View device
              </a>
            ) : (
              <span className="text-right font-medium">—</span>
            )}
          </div>
        </CollapsePanel>

        {agent.primary_contact_name && (
          <CollapsePanel
            id="overview-primary-contact"
            title="Primary contact"
            collapsedSummary={agent.primary_contact_name}
            open={contactOpen}
            onOpenChange={setContactOpen}
            bodyClassName="text-sm space-y-1"
          >
            <p className="font-medium">{agent.primary_contact_name}</p>
            {agent.primary_contact_email && (
              <a
                href={`mailto:${agent.primary_contact_email}`}
                className="text-primary hover:underline"
              >
                {agent.primary_contact_email}
              </a>
            )}
          </CollapsePanel>
        )}
      </div>

      <PostureExclusionsCard
        agent={agent}
        onSaved={(patch) => onAgentPatch?.(patch)}
      />
    </div>
  )
}
